import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  CirclePlus,
  Copy,
  Crown,
  Film,
  Loader2,
  PencilRuler,
  Search,
  Sparkles,
  Star,
  Trash2,
  X,
} from "lucide-react";
import {
  createTemplateMetadata,
  deleteTemplate,
  getTemplateById,
  getTemplates,
  updateTemplateMetadata,
} from "../api/templates";
import { createTemplateAsset, getTemplateAssets, updateTemplateAsset } from "../api/templateAssets";
import { generateAiTemplatePlan } from "../api/adminAiTemplates";
import { getAssetLibraryItems } from "../api/assetLibrary";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../components/LoginModal";
import { getTemplateAssetPlan, getTemplateAssetRefs } from "../data/templateAssetRegistry";
import { buildRoyalWeddingDraft as buildRoyalWeddingDraftFromSpec } from "../data/templateDraftBuilders";

const EVENT_TYPE_OPTIONS = [
  { value: "wedding", label: "Wedding" },
  { value: "birthday", label: "Birthday" },
  { value: "baby_shower", label: "Baby Shower" },
  { value: "graduation", label: "Graduation" },
  { value: "housewarming", label: "Housewarming" },
  { value: "cultural_programs", label: "Cultural Programs" },
  { value: "engagement", label: "Engagement" },
  { value: "corporate", label: "Corporate" },
];

const STARTER_TEMPLATE_METADATA = {
  wedding: {
    category: "Wedding",
    style_tags: ["traditional", "royal", "luxury"],
    subevent_types: ["mehendi", "sangeet", "wedding", "reception"],
  },
  birthday: {
    category: "Birthday",
    style_tags: ["playful", "festive", "modern"],
  },
  baby_shower: {
    category: "Baby Shower",
    style_tags: ["soft", "heartfelt", "family"],
  },
  graduation: {
    category: "Graduation",
    style_tags: ["modern", "proud", "clean"],
  },
  housewarming: {
    category: "Housewarming",
    style_tags: ["warm", "traditional", "family"],
  },
  cultural_programs: {
    category: "Cultural Programs",
    style_tags: ["cultural", "performance", "festive"],
  },
  engagement: {
    category: "Engagement",
    style_tags: ["romantic", "elegant", "modern"],
  },
  corporate: {
    category: "Corporate",
    style_tags: ["professional", "modern", "clean"],
  },
};

const ROYAL_WEDDING_SCENES = [
  {
    id: "grand_opening",
    label: "Grand Opening",
    purpose: "Hero names scene with premium first impression.",
  },
  {
    id: "invitation_message",
    label: "Invitation Message",
    purpose: "Formal wedding invitation copy.",
  },
  {
    id: "ceremony_details",
    label: "Ceremony Details",
    purpose: "Date, time, and venue summary.",
  },
  {
    id: "venue_address",
    label: "Venue Address",
    purpose: "Expanded address block for guests.",
  },
  {
    id: "closing_rsvp",
    label: "Closing and RSVP",
    purpose: "Blessing line and RSVP handoff.",
  },
];

const DEFAULT_CREATE_FORM = {
  name: "",
  event_type: "wedding",
  category: "Wedding",
  style_tags_text: "",
  culture_tags_text: "",
  religion_tags_text: "",
  language_tags_text: "",
  subevent_types_text: "",
  is_featured: false,
  is_premium: false,
};

export const DEFAULT_AI_CREATE_FORM = {
  template_name: "",
  event_type: "wedding",
  style_family: "royal",
  format: "mobile_video",
  scene_count: 5,
  palette_text: "maroon, antique gold, ivory",
  palette_primary: "#5a1029",
  palette_secondary: "#8a1738",
  palette_accent: "#d4af37",
  palette_background: "#2d0817",
  palette_text_color: "#fff2d4",
  style_preset_id: "maroon-gold-palace",
  style_prompt:
    "Create a royal Indian wedding mobile video invitation with a premium palace feel. Keep the opening grand and details pages readable.",
  mood_text: "regal, cinematic, traditional",
  typography_direction: "Elegant serif display typography with clean readable detail text.",
  ornament_density: "rich",
  asset_creation_mode: "reuse_only",
  copy_style: "formal, warm",
  cultural_context_text: "Indian wedding",
  selected_assets: [],
  scene_instructions: [],
  notes: "",
  is_featured: false,
  is_premium: false,
};

export const ROYAL_WEDDING_ASSET_FAMILY_ID = "royal_wedding_mobile_video_v1";

export const ROYAL_WEDDING_STYLE_PRESETS = [
  {
    id: "maroon-gold-palace",
    label: "Maroon Gold Palace",
    mood: "regal, cinematic, traditional",
    palette: {
      primary: "#5a1029",
      secondary: "#8a1738",
      accent: "#d4af37",
      background: "#2d0817",
      text: "#fff2d4",
    },
    prompt:
      "Create a royal Indian wedding mobile video invitation with a premium palace feel. Keep the opening grand and details pages readable.",
    typography: "High-contrast elegant serif display typography with readable classic serif detail text.",
    ornamentDensity: "ornate",
  },
  {
    id: "emerald-mehendi-royal",
    label: "Emerald Mehendi Royal",
    mood: "festive, rich, elegant",
    palette: {
      primary: "#0f3d35",
      secondary: "#1f6b52",
      accent: "#d8b85a",
      background: "#08231f",
      text: "#f8eed3",
    },
    prompt:
      "Create a royal Indian wedding invite with emerald and antique gold colors, mehendi-inspired patterns, and a premium festive mood.",
    typography: "Ornamental serif with graceful curves, paired with clean detail text.",
    ornamentDensity: "rich",
  },
  {
    id: "ivory-floral-luxury",
    label: "Ivory Floral Luxury",
    mood: "romantic, refined, graceful",
    palette: {
      primary: "#f5ead8",
      secondary: "#c9a875",
      accent: "#b76e79",
      background: "#fff8ec",
      text: "#563d2d",
    },
    prompt:
      "Create a soft luxury Indian wedding invite with ivory, champagne gold, blush floral accents, and refined spacing.",
    typography: "Romantic serif or calligraphic serif with warm readable body text.",
    ornamentDensity: "balanced",
  },
];

function formatAssetStrategy(strategy) {
  return (strategy || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getAssetKey(asset) {
  return asset?.assetKey || asset?.asset_key || asset?.id || "";
}

function getAssetDisplayName(asset) {
  return asset?.displayName || asset?.display_name || asset?.assetKey || asset?.asset_key || "Untitled asset";
}

function getAssetGenerationPrompt(asset) {
  return asset?.generationPrompt || asset?.generation_prompt || "";
}

function getAssetNegativePrompt(asset) {
  return asset?.negativePrompt || asset?.negative_prompt || "";
}

function getAssetPreviewUrl(asset) {
  if (asset?.thumbnail_url || asset?.file_url) {
    return asset.thumbnail_url || asset.file_url;
  }

  const registryAsset = getTemplateAssetRefs(asset?.family_id)?.[asset?.role];
  return registryAsset?.src || "";
}

function getAssetMediaKind(asset) {
  const type = (asset?.asset_type || "").toLowerCase();
  const url = getAssetPreviewUrl(asset).toLowerCase();

  if (type.includes("video") || /\.(mp4|webm|mov)(\?|#|$)/.test(url)) {
    return "video";
  }

  if (type.includes("audio") || /\.(mp3|wav|ogg|m4a)(\?|#|$)/.test(url)) {
    return "audio";
  }

  if (
    type.includes("image") ||
    type.includes("svg") ||
    /\.(svg|png|jpe?g|gif|webp|avif)(\?|#|$)/.test(url)
  ) {
    return "image";
  }

  return url ? "link" : "empty";
}

export function getLibraryAssetRoleGuess(asset) {
  const tags = [
    ...(asset?.usage_tags || []),
    ...(asset?.style_tags || []),
    asset?.name || "",
    asset?.asset_type || "",
  ]
    .join(" ")
    .toLowerCase();

  if (tags.includes("arch")) return "royal_arch";
  if (tags.includes("mandala")) return "mandala_centerpiece";
  if (tags.includes("divider")) return "divider_ornament";
  if (tags.includes("paisley") || tags.includes("corner")) return "paisley_corner";
  if (tags.includes("floral") || tags.includes("flower")) return "floral_cluster";
  if (tags.includes("sparkle")) return "sparkle_overlay";
  if (tags.includes("background")) return "hero_background";
  return "decorative_asset";
}

export function getCreativeBriefPalette(form) {
  return {
    primary: form.palette_primary,
    secondary: form.palette_secondary,
    accent: form.palette_accent,
    background: form.palette_background,
    text: form.palette_text_color,
  };
}

export function mapRegistryAssetToCreatePayload(asset) {
  return {
    family_id: asset.familyId,
    spec_id: asset.specId,
    asset_key: asset.assetKey,
    role: asset.role,
    display_name: asset.displayName,
    asset_type: asset.type,
    source_strategy: asset.sourceStrategy,
    status: asset.src ? (asset.sourceStrategy === "generate" ? "needs_review" : "available") : "planned",
    is_required: Boolean(asset.required),
    is_reusable: Boolean(asset.reusable),
    description: asset.description || "",
    prompt_reference: asset.promptReference || "",
    generation_prompt: asset.generationPrompt || "",
    negative_prompt: asset.negativePrompt || "",
    asset_tag_query: asset.assetTagQuery || [],
    scene_usage: asset.sceneUsage || [],
    storage_path: asset.storagePath || "",
    file_url: asset.src || null,
    thumbnail_url: null,
    polotno_mapping: asset.polotnoMapping || null,
    asset_metadata: {
      seeded_from_registry: true,
      is_placeholder: Boolean(asset.placeholder),
      editable: asset.editable !== false,
    },
  };
}

function tagsToText(tags) {
  return Array.isArray(tags) ? tags.join(", ") : "";
}

function textToTags(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeTemplateForForm(template) {
  return {
    ...template,
    event_type: template?.event_type || "",
    subevent_types: template?.subevent_types || [],
    style_tags: template?.style_tags || [],
    culture_tags: template?.culture_tags || [],
    religion_tags: template?.religion_tags || [],
    language_tags: template?.language_tags || [],
    preview_video_url: template?.preview_video_url || "",
    is_featured: Boolean(template?.is_featured),
    is_premium: Boolean(template?.is_premium),
  };
}

function isStarterTemplateUsingBasePreview(template) {
  const name = (template?.name || "").toLowerCase();
  return name.includes("starter template");
}

export function AdminTemplateMetadataPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, isAuthLoading } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formState, setFormState] = useState(null);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isLoadingTemplateAssets, setIsLoadingTemplateAssets] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusTone, setStatusTone] = useState("idle");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isAiCreateModalOpen, setIsAiCreateModalOpen] = useState(false);
  const [isAiCreating, setIsAiCreating] = useState(false);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [isGeneratingStarters, setIsGeneratingStarters] = useState(false);
  const [createForm, setCreateForm] = useState(DEFAULT_CREATE_FORM);
  const [aiCreateForm, setAiCreateForm] = useState(DEFAULT_AI_CREATE_FORM);
  const [savedTemplateAssets, setSavedTemplateAssets] = useState([]);
  const [assetLibraryItems, setAssetLibraryItems] = useState([]);
  const [isLoadingAssetLibrary, setIsLoadingAssetLibrary] = useState(false);
  const [savingTemplateAssetId, setSavingTemplateAssetId] = useState(null);
  const [copiedAssetKey, setCopiedAssetKey] = useState("");
  const [copiedAssetUrlKey, setCopiedAssetUrlKey] = useState("");

  const loadTemplates = useCallback(async () => {
    try {
      setIsLoadingTemplates(true);
      const data = await getTemplates({ limit: 100 });
      setTemplates(data || []);

      if (data?.length) {
        setSelectedTemplateId((currentId) => currentId || data[0].id);
      }
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to load templates.");
    } finally {
      setIsLoadingTemplates(false);
    }
  }, []);

  const loadTemplateDetails = useCallback(async (templateId, { preserveStatus = false } = {}) => {
    if (!templateId) {
      setSavedTemplateAssets([]);
      return;
    }

    try {
      setIsLoadingTemplate(true);
      setIsLoadingTemplateAssets(true);
      if (!preserveStatus) {
        setStatusMessage("");
      }
      const [data, assetRows] = await Promise.all([
        getTemplateById(templateId),
        getTemplateAssets(templateId).catch((error) => {
          console.error("Unable to load template assets:", error);
          return [];
        }),
      ]);
      const normalizedTemplate = normalizeTemplateForForm(data);
      setSelectedTemplate(normalizedTemplate);
      setSavedTemplateAssets(assetRows || []);
      setFormState({
        ...normalizedTemplate,
        subevent_types_text: tagsToText(normalizedTemplate.subevent_types),
        style_tags_text: tagsToText(normalizedTemplate.style_tags),
        culture_tags_text: tagsToText(normalizedTemplate.culture_tags),
        religion_tags_text: tagsToText(normalizedTemplate.religion_tags),
        language_tags_text: tagsToText(normalizedTemplate.language_tags),
      });

      setTemplates((currentTemplates) =>
        currentTemplates.map((template) =>
          template.id === normalizedTemplate.id ? { ...template, ...normalizedTemplate } : template
        )
      );
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to load the selected template.");
    } finally {
      setIsLoadingTemplate(false);
      setIsLoadingTemplateAssets(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadTemplateDetails(selectedTemplateId);
  }, [loadTemplateDetails, selectedTemplateId]);

  useEffect(() => {
    if (!isAiCreateModalOpen || !isAuthenticated || !user?.is_admin) {
      return;
    }

    let isMounted = true;

    async function loadAssetLibraryForBrief() {
      setIsLoadingAssetLibrary(true);
      try {
        const items = await getAssetLibraryItems({ limit: 100, status: "available" });
        if (isMounted) {
          setAssetLibraryItems(items || []);
        }
      } catch (error) {
        console.error("Unable to load asset library for AI brief:", error);
        if (isMounted) {
          setAssetLibraryItems([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingAssetLibrary(false);
        }
      }
    }

    loadAssetLibraryForBrief();

    return () => {
      isMounted = false;
    };
  }, [isAiCreateModalOpen, isAuthenticated, user?.is_admin]);

  useEffect(() => {
    if (!selectedTemplateId) {
      return;
    }

    const refreshSelectedTemplate = () => {
      if (document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      loadTemplates();
      loadTemplateDetails(selectedTemplateId, { preserveStatus: true });
    };

    window.addEventListener("focus", refreshSelectedTemplate);
    document.addEventListener("visibilitychange", refreshSelectedTemplate);

    return () => {
      window.removeEventListener("focus", refreshSelectedTemplate);
      document.removeEventListener("visibilitychange", refreshSelectedTemplate);
    };
  }, [loadTemplateDetails, loadTemplates, selectedTemplateId]);

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return templates;
    }

    return templates.filter((template) =>
      `${template.name || ""}`.toLowerCase().includes(normalizedQuery)
    );
  }, [searchQuery, templates]);

  const handleFieldChange = (field, value) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
    setStatusMessage("");
  };

  const handleCreateFieldChange = (field, value) => {
    setCreateForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleAiCreateFieldChange = (field, value) => {
    setAiCreateForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const applyAiStylePreset = (presetId) => {
    const preset = ROYAL_WEDDING_STYLE_PRESETS.find((item) => item.id === presetId);
    setAiCreateForm((current) => {
      if (!preset) {
        return {
          ...current,
          style_preset_id: presetId,
        };
      }

      return {
        ...current,
        style_preset_id: preset.id,
        style_prompt: preset.prompt,
        mood_text: preset.mood,
        typography_direction: preset.typography,
        ornament_density: preset.ornamentDensity,
        palette_primary: preset.palette.primary,
        palette_secondary: preset.palette.secondary,
        palette_accent: preset.palette.accent,
        palette_background: preset.palette.background,
        palette_text_color: preset.palette.text,
        palette_text: [
          preset.label,
          preset.palette.primary,
          preset.palette.secondary,
          preset.palette.accent,
          preset.palette.background,
          preset.palette.text,
        ].join(", "),
      };
    });
  };

  const handleAiAssetToggle = (asset) => {
    setAiCreateForm((current) => {
      const assetId = String(asset.id);
      const existingAssets = current.selected_assets || [];
      const isSelected = existingAssets.some(
        (selectedAsset) => String(selectedAsset.asset_library_item_id) === assetId
      );

      if (isSelected) {
        return {
          ...current,
          selected_assets: existingAssets.filter(
            (selectedAsset) => String(selectedAsset.asset_library_item_id) !== assetId
          ),
        };
      }

      return {
        ...current,
        selected_assets: [
          ...existingAssets,
          {
            asset_library_item_id: asset.id,
            asset_name: asset.name,
            role: getLibraryAssetRoleGuess(asset),
            usage_mode: "reference_only",
            instruction: "Use this as visual inspiration for the generated draft.",
            target_scenes_text: "",
            strength: "medium",
          },
        ],
      };
    });
  };

  const handleAiSelectedAssetFieldChange = (assetId, field, value) => {
    setAiCreateForm((current) => ({
      ...current,
      selected_assets: (current.selected_assets || []).map((selectedAsset) =>
        String(selectedAsset.asset_library_item_id) === String(assetId)
          ? { ...selectedAsset, [field]: value }
          : selectedAsset
      ),
    }));
  };

  const handleSavedAssetFieldChange = (assetId, field, value) => {
    setSavedTemplateAssets((currentAssets) =>
      currentAssets.map((asset) => (asset.id === assetId ? { ...asset, [field]: value } : asset))
    );
    setStatusMessage("");
  };

  const handleSaveTemplateAsset = async (asset) => {
    if (!asset?.id) {
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    setSavingTemplateAssetId(asset.id);
    setStatusMessage("");

    try {
      const savedAsset = await updateTemplateAsset(asset.id, {
        status: asset.status || "planned",
        file_url: asset.file_url || null,
        storage_path: asset.storage_path || null,
        thumbnail_url: asset.thumbnail_url || null,
      });

      setSavedTemplateAssets((currentAssets) =>
        currentAssets.map((currentAsset) =>
          currentAsset.id === savedAsset.id ? savedAsset : currentAsset
        )
      );
      setStatusTone("success");
      setStatusMessage(`Saved asset "${getAssetDisplayName(savedAsset)}".`);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to save template asset.");
    } finally {
      setSavingTemplateAssetId(null);
    }
  };

  const handleCopyAssetUrl = async (asset) => {
    const previewUrl = getAssetPreviewUrl(asset);
    if (!previewUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(previewUrl);
      setCopiedAssetUrlKey(getAssetKey(asset));
      window.setTimeout(() => setCopiedAssetUrlKey(""), 1800);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage("Unable to copy the asset URL.");
    }
  };

  const submitMetadata = async () => {
    setIsSaving(true);
    setStatusMessage("");

    try {
      const payload = normalizeTemplateForForm({
        ...selectedTemplate,
        ...formState,
        subevent_types: textToTags(formState.subevent_types_text || ""),
        style_tags: textToTags(formState.style_tags_text || ""),
        culture_tags: textToTags(formState.culture_tags_text || ""),
        religion_tags: textToTags(formState.religion_tags_text || ""),
        language_tags: textToTags(formState.language_tags_text || ""),
      });

      const savedTemplate = await updateTemplateMetadata(payload);
      const normalizedTemplate = normalizeTemplateForForm(savedTemplate);

      setSelectedTemplate(normalizedTemplate);
      setFormState({
        ...normalizedTemplate,
        subevent_types_text: tagsToText(normalizedTemplate.subevent_types),
        style_tags_text: tagsToText(normalizedTemplate.style_tags),
        culture_tags_text: tagsToText(normalizedTemplate.culture_tags),
        religion_tags_text: tagsToText(normalizedTemplate.religion_tags),
        language_tags_text: tagsToText(normalizedTemplate.language_tags),
      });

      setTemplates((currentTemplates) =>
        currentTemplates.map((template) =>
          template.id === normalizedTemplate.id ? { ...template, ...normalizedTemplate } : template
        )
      );

      setStatusTone("success");
      setStatusMessage("Template metadata saved.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to save template metadata.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formState) {
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    await submitMetadata();
  };

  const handlePreview = () => {
    if (!selectedTemplateId) return;

    navigate(`/preview/${selectedTemplateId}`, {
      state: {
        templateId: selectedTemplateId,
        mode: "template",
        returnTo: "/admin/templates",
        returnLabel: "Templates",
      },
    });
  };

  const handleOpenEditor = () => {
    if (!selectedTemplateId) return;

    navigate(`/editor/${selectedTemplateId}`, {
      state: {
        mode: "template",
      },
    });
  };

  const handleDeleteSelectedTemplate = async () => {
    if (!selectedTemplateId || !selectedTemplate) {
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    const confirmed = window.confirm(
      `Delete "${selectedTemplate.name}"?\n\nThis removes the template metadata and Polotno JSON. Templates already linked to real events will be protected by the backend.`
    );

    if (!confirmed) {
      return;
    }

    setIsDeletingTemplate(true);
    setStatusMessage("");

    try {
      await deleteTemplate(selectedTemplateId);
      const nextTemplates = templates.filter((template) => template.id !== selectedTemplateId);
      setTemplates(nextTemplates);
      setSelectedTemplateId(nextTemplates[0]?.id || null);
      setSelectedTemplate(null);
      setSavedTemplateAssets([]);
      setFormState(null);
      setStatusTone("success");
      setStatusMessage(`Deleted template "${selectedTemplate.name}".`);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to delete template.");
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const openTemplateEditor = (templateId, draftJson = null, { hardRedirect = false } = {}) => {
    if (!templateId) {
      return;
    }

    const editorPath = `/editor/${templateId}`;
    if (draftJson) {
      window.sessionStorage.setItem(`template-editor-draft:${templateId}`, JSON.stringify(draftJson));
    }
    window.sessionStorage.setItem(`template-editor-mode:${templateId}`, "template");

    if (hardRedirect) {
      window.location.assign(editorPath);
      return;
    }

    navigate(editorPath, {
      state: draftJson
        ? {
            mode: "template",
            jsonData: draftJson,
          }
        : {
            mode: "template",
          },
    });
  };

  const handleClearInheritedPreviews = async () => {
    if (!selectedTemplate) {
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    setIsSaving(true);
    setStatusMessage("");

    try {
      const payload = {
        ...selectedTemplate,
        thumbnail_image: null,
        background_image: null,
        preview_video_url: null,
      };

      const savedTemplate = await updateTemplateMetadata(payload);
      const normalizedTemplate = normalizeTemplateForForm(savedTemplate);

      setSelectedTemplate(normalizedTemplate);
      setFormState({
        ...normalizedTemplate,
        subevent_types_text: tagsToText(normalizedTemplate.subevent_types),
        style_tags_text: tagsToText(normalizedTemplate.style_tags),
        culture_tags_text: tagsToText(normalizedTemplate.culture_tags),
        religion_tags_text: tagsToText(normalizedTemplate.religion_tags),
        language_tags_text: tagsToText(normalizedTemplate.language_tags),
      });
      setTemplates((currentTemplates) =>
        currentTemplates.map((template) =>
          template.id === normalizedTemplate.id ? { ...template, ...normalizedTemplate } : template
        )
      );

      setStatusTone("success");
      setStatusMessage("Inherited preview assets cleared for this starter template.");
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to clear inherited preview assets.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateStarterTemplates = async () => {
    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    const baseTemplate = selectedTemplate || templates[0];
    if (!baseTemplate) {
      setStatusTone("error");
      setStatusMessage("Select or create a base template first.");
      return;
    }

    const existingTypes = new Set(
      templates
        .map((template) => (template?.event_type || "").trim().toLowerCase())
        .filter(Boolean)
    );

    const missingTypes = EVENT_TYPE_OPTIONS.filter(
      (option) => option.value !== "wedding" && !existingTypes.has(option.value)
    );

    if (!missingTypes.length) {
      setStatusTone("success");
      setStatusMessage("Starter templates already exist for the supported event types.");
      return;
    }

    setIsGeneratingStarters(true);
    setStatusMessage("");

    try {
      const createdTemplates = [];

      for (const option of missingTypes) {
        const starterConfig = STARTER_TEMPLATE_METADATA[option.value] || {};
        const payload = {
          ...baseTemplate,
          id: undefined,
          name: `${option.label} Starter Template`,
          category: starterConfig.category || option.label,
          event_type: option.value,
          style_tags: starterConfig.style_tags || baseTemplate.style_tags || [],
          subevent_types: starterConfig.subevent_types || [],
          thumbnail_image: null,
          background_image: null,
          preview_video_url: null,
          is_featured: false,
          is_premium: false,
        };

        const createdTemplate = await createTemplateMetadata(payload);
        createdTemplates.push(normalizeTemplateForForm(createdTemplate));
      }

      if (createdTemplates.length) {
        setTemplates((currentTemplates) => [...currentTemplates, ...createdTemplates]);
        setStatusTone("success");
        setStatusMessage(
          `Created ${createdTemplates.length} starter templates from "${baseTemplate.name}".`
        );
      }
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to generate starter templates.");
    } finally {
      setIsGeneratingStarters(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm(DEFAULT_CREATE_FORM);
  };

  const resetAiCreateForm = () => {
    setAiCreateForm(DEFAULT_AI_CREATE_FORM);
  };

  const submitCreateTemplate = async () => {
    if (!createForm.name.trim()) {
      setStatusTone("error");
      setStatusMessage("Template name is required.");
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    setIsCreating(true);
    setStatusMessage("");

    try {
      const payload = {
        name: createForm.name.trim(),
        category: createForm.category.trim() || null,
        event_type: createForm.event_type || null,
        htmltext: "",
        tags: [],
        style_tags: textToTags(createForm.style_tags_text || ""),
        culture_tags: textToTags(createForm.culture_tags_text || ""),
        religion_tags: textToTags(createForm.religion_tags_text || ""),
        language_tags: textToTags(createForm.language_tags_text || ""),
        subevent_types: textToTags(createForm.subevent_types_text || ""),
        is_featured: Boolean(createForm.is_featured),
        is_premium: Boolean(createForm.is_premium),
      };

      const createdTemplate = await createTemplateMetadata(payload);
      const normalizedTemplate = normalizeTemplateForForm(createdTemplate);

      setTemplates((currentTemplates) => [normalizedTemplate, ...currentTemplates]);
      setIsCreateModalOpen(false);
      resetCreateForm();
      openTemplateEditor(normalizedTemplate.id, null, { hardRedirect: true });
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create template.");
    } finally {
      setIsCreating(false);
    }
  };

  const aiScenePreview = useMemo(
    () => ROYAL_WEDDING_SCENES.slice(0, Math.max(1, Math.min(5, Number(aiCreateForm.scene_count) || 5))),
    [aiCreateForm.scene_count]
  );
  const aiAssetPlan = useMemo(
    () => getTemplateAssetPlan(ROYAL_WEDDING_ASSET_FAMILY_ID),
    []
  );

  const handleCopyAssetPrompt = async (asset) => {
    const generationPrompt = getAssetGenerationPrompt(asset);
    const negativePrompt = getAssetNegativePrompt(asset);
    const promptText = [
      generationPrompt,
      negativePrompt ? `Negative prompt: ${negativePrompt}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(promptText);
      setCopiedAssetKey(getAssetKey(asset));
      window.setTimeout(() => setCopiedAssetKey(""), 1800);
    } catch (error) {
      setStatusTone("error");
      setStatusMessage("Unable to copy the prompt. Please copy it manually from the asset plan.");
    }
  };

  const submitAiCreateTemplate = async () => {
    if (!aiCreateForm.template_name.trim()) {
      setStatusTone("error");
      setStatusMessage("Template name is required for AI-assisted creation.");
      return;
    }

    if (aiCreateForm.event_type !== "wedding" || aiCreateForm.style_family !== "royal") {
      setStatusTone("error");
      setStatusMessage("This beta flow currently supports only the Royal Wedding spec family.");
      return;
    }

    if (!isAuthenticated) {
      setShowLogin(true);
      return;
    }

    setIsAiCreating(true);
    setStatusTone("idle");
    setStatusMessage("Generating the template plan...");

    try {
      const selectedAssetsForRequest = (aiCreateForm.selected_assets || []).map((asset) => ({
        asset_library_item_id: asset.asset_library_item_id,
        asset_name: asset.asset_name,
        role: asset.role,
        usage_mode: asset.usage_mode,
        instruction: asset.instruction,
        target_scenes: textToTags(asset.target_scenes_text || ""),
        strength: asset.strength || "medium",
      }));
      const palette = getCreativeBriefPalette(aiCreateForm);
      const planningResult = await generateAiTemplatePlan({
        ...aiCreateForm,
        template_name: aiCreateForm.template_name.trim(),
        palette_text: aiCreateForm.palette_text.trim() || "maroon, antique gold, ivory",
        palette,
        style_prompt: aiCreateForm.style_prompt.trim(),
        mood_text: aiCreateForm.mood_text.trim(),
        typography_direction: aiCreateForm.typography_direction.trim(),
        ornament_density: aiCreateForm.ornament_density,
        copy_style: aiCreateForm.copy_style.trim() || "formal, warm",
        cultural_context_text: aiCreateForm.cultural_context_text.trim() || "Indian wedding",
        selected_assets: selectedAssetsForRequest,
        scene_count: Number(aiCreateForm.scene_count) || 5,
      });
      const plan = planningResult?.plan;
      const draftJson = buildRoyalWeddingDraftFromSpec({
        templateName: aiCreateForm.template_name.trim(),
        paletteText: aiCreateForm.palette_text.trim() || "maroon, antique gold, ivory",
        copyStyle: aiCreateForm.copy_style.trim() || "formal, warm",
        culturalContextText: aiCreateForm.cultural_context_text.trim() || "Indian wedding",
        plan,
      });
      const planSourceTag =
        planningResult?.source === "openai"
          ? "openai_planned"
          : planningResult?.source === "gemini"
          ? "gemini_planned"
          : "fallback_planned";
      const providerLabel =
        planningResult?.source === "openai"
          ? "OpenAI-planned"
          : planningResult?.source === "gemini"
          ? "Gemini-planned"
          : "Fallback-planned";

      const payload = {
        name: aiCreateForm.template_name.trim(),
        category: "Wedding",
        event_type: "wedding",
        htmltext: JSON.stringify(draftJson),
        tags: ["ai_draft", "spec_driven", "admin_generated", planSourceTag],
        style_tags: [
          "royal",
          "luxury",
          "traditional",
          "ai-draft",
          aiCreateForm.style_preset_id,
          ...textToTags(aiCreateForm.mood_text || ""),
        ].filter(Boolean),
        culture_tags: textToTags(aiCreateForm.cultural_context_text || ""),
        religion_tags: [],
        language_tags: [],
        subevent_types: ["mehendi", "sangeet", "wedding", "reception"],
        is_featured: Boolean(aiCreateForm.is_featured),
        is_premium: Boolean(aiCreateForm.is_premium),
      };

      const createdTemplate = await createTemplateMetadata(payload);
      const normalizedTemplate = normalizeTemplateForForm(createdTemplate);
      let seededAssetCount = 0;

      try {
        await Promise.all(
          aiAssetPlan.map((asset) =>
            createTemplateAsset(normalizedTemplate.id, {
              ...mapRegistryAssetToCreatePayload(asset),
              asset_metadata: {
                ...(mapRegistryAssetToCreatePayload(asset).asset_metadata || {}),
                creative_brief: {
                  style_preset_id: aiCreateForm.style_preset_id,
                  palette,
                  selected_assets: selectedAssetsForRequest,
                },
              },
            })
          )
        );
        seededAssetCount = aiAssetPlan.length;
      } catch (assetError) {
        console.error("Failed to seed template asset plan:", assetError);
      }

      setTemplates((currentTemplates) => [normalizedTemplate, ...currentTemplates]);
      setIsAiCreateModalOpen(false);
      resetAiCreateForm();
      setStatusTone("success");
      setStatusMessage(
        seededAssetCount
          ? `${providerLabel} royal wedding draft created with ${seededAssetCount} asset roles. Opening Polotno for refinement.`
          : `${providerLabel} royal wedding draft created, but asset roles were not saved. Opening Polotno for refinement.`
      );
      openTemplateEditor(normalizedTemplate.id, draftJson, { hardRedirect: true });
    } catch (error) {
      setStatusTone("error");
      setStatusMessage(error?.message || "Unable to create AI-assisted draft template.");
    } finally {
      setIsAiCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fff8fb] text-[#251c25]">
      <section className="border-b border-[#f2dfea] bg-[linear-gradient(180deg,#fff8fb_0%,#fff2f8_100%)]">
        <div className="mx-auto w-full max-w-[1480px] px-4 py-10 md:px-8 xl:px-10">
          <div className="flex flex-col gap-8 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#b07c98]">
                Template admin
              </p>
              <h1 className="mt-3 font-[Georgia] text-[clamp(2rem,4vw,3.7rem)] font-black leading-tight text-[#241824]">
                Manage template discovery metadata
              </h1>
              <p className="mt-4 text-base leading-7 text-[#6f5a67] md:text-lg">
                Keep template discovery, dynamic event flows, and preview merchandising aligned by
                editing the metadata that powers them.
              </p>
            </div>

            <div className="rounded-[28px] border border-[#efd9e4] bg-white/90 px-5 py-4 shadow-sm">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                Signed in
              </p>
              <p className="mt-2 text-sm font-semibold text-[#3e2935]">
                {user?.name || user?.email || user?.phone || "Admin session"}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#ff0099] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black"
            >
              <CirclePlus size={16} />
              Create Blank Template
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/templates/ai-create")}
              className="inline-flex items-center gap-2 rounded-full bg-[#2b1d27] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#2b1d27]/20 transition-colors hover:bg-[#ff0099]"
            >
              <Sparkles size={16} />
              Create Template with AI
            </button>
            <button
              type="button"
              onClick={() => navigate("/admin/assets")}
              className="inline-flex items-center gap-2 rounded-full border border-[#ecd2df] bg-white px-5 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
            >
              <PencilRuler size={16} />
              Asset Library
            </button>
            <button
              type="button"
              onClick={handleGenerateStarterTemplates}
              disabled={isGeneratingStarters || isLoadingTemplates}
              className="inline-flex items-center gap-2 rounded-full border border-[#ecd2df] bg-white px-5 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Sparkles size={16} />
              {isGeneratingStarters ? "Generating starter templates..." : "Generate Starter Templates"}
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-[1480px] gap-6 px-4 py-8 md:px-8 xl:grid-cols-[320px_minmax(0,1fr)] xl:px-10">
        <aside className="rounded-[30px] border border-[#edd9e4] bg-white p-5 shadow-[0_18px_50px_rgba(86,43,66,0.08)]">
          <div className="flex items-center gap-3 rounded-full border border-[#efcddd] bg-[#fff9fc] px-4 py-3">
            <Search className="h-4 w-4 text-[#b13771]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search templates"
              className="w-full bg-transparent text-sm text-[#3d2e38] outline-none placeholder:text-[#b29aa8]"
            />
          </div>

          <div className="mt-5 flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
              Templates
            </p>
            <span className="text-xs font-semibold text-[#8e6d80]">
              {filteredTemplates.length}
            </span>
          </div>

          <div className="mt-4 space-y-3">
            {isLoadingTemplates ? (
              <div className="flex items-center gap-2 rounded-2xl bg-[#fff6fa] px-4 py-4 text-sm text-[#8a7080]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading templates...
              </div>
            ) : filteredTemplates.length ? (
              filteredTemplates.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`w-full rounded-[24px] border px-4 py-4 text-left transition-all ${
                    selectedTemplateId === template.id
                      ? "border-[#ff0099] bg-[#fff1f8] shadow-md shadow-[#ff0099]/10"
                      : "border-[#f0dde8] bg-white hover:border-[#efcddd] hover:bg-[#fff9fc]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-[#2e1d28]">{template.name}</p>
                      <p className="mt-1 text-xs text-[#8d6d7f]">
                        {(template.event_type || template.category || "Uncategorized")
                          .toString()
                          .replace(/_/g, " ")}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {template.is_featured ? (
                        <span className="rounded-full bg-[#fff0b8] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#876300]">
                          Featured
                        </span>
                      ) : null}
                      {template.is_premium ? (
                        <span className="rounded-full bg-[#f7ebff] px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#7d2eb8]">
                          Premium
                        </span>
                      ) : null}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-[#eed7e3] bg-[#fff9fc] px-4 py-5 text-sm text-[#8a7080]">
                No templates matched that search.
              </div>
            )}
          </div>
        </aside>

        <div className="space-y-6">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <div className="rounded-[30px] border border-[#edd9e4] bg-white p-5 shadow-[0_18px_50px_rgba(86,43,66,0.08)]">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                    Template preview
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-[#251a24]">
                    {selectedTemplate?.name || "Select a template"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={!selectedTemplateId}
                  className="inline-flex items-center gap-2 rounded-full border border-[#ecd2df] bg-white px-4 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Film size={16} />
                  Preview
                </button>
              </div>

              <div className="mb-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleOpenEditor}
                  disabled={!selectedTemplateId || isDeletingTemplate}
                  className="inline-flex items-center gap-2 rounded-full bg-[#2b1d27] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <PencilRuler size={16} />
                  Open in Editor
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelectedTemplate}
                  disabled={!selectedTemplateId || isDeletingTemplate || isSaving}
                  className="inline-flex items-center gap-2 rounded-full border border-[#f0b9c8] bg-[#fff7f8] px-4 py-3 text-sm font-bold text-[#b42345] transition-colors hover:border-[#d92d20] hover:bg-[#fff1f0] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isDeletingTemplate ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 size={16} />
                  )}
                  {isDeletingTemplate ? "Deleting..." : "Delete Template"}
                </button>
                {isStarterTemplateUsingBasePreview(selectedTemplate) ? (
                  <button
                    type="button"
                    onClick={handleClearInheritedPreviews}
                    disabled={!selectedTemplateId || isSaving || isDeletingTemplate}
                    className="inline-flex items-center gap-2 rounded-full border border-[#ecd2df] bg-white px-4 py-3 text-sm font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X size={16} />
                    {isSaving ? "Clearing..." : "Clear Preview Assets"}
                  </button>
                ) : null}
              </div>

              <div className="overflow-hidden rounded-[24px] border border-[#f2dce8] bg-[#fdf4f8]">
                <div
                  key={`template-preview-${selectedTemplate?.id || "empty"}-${
                    selectedTemplate?.thumbnail_image || "no-thumb"
                  }`}
                  className="relative aspect-[4/5]"
                >
                  {selectedTemplate?.thumbnail_image ? (
                    <img
                      src={selectedTemplate.thumbnail_image}
                      alt={selectedTemplate.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-[#8a7080]">
                      No thumbnail available
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/80">
                      {(selectedTemplate?.event_type || "template").toString().replace(/_/g, " ")}
                    </div>
                    <h3 className="mt-2 text-2xl font-black leading-tight">
                      {selectedTemplate?.name || "Template preview"}
                    </h3>
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[20px] bg-[#fff7fb] px-4 py-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                    Preview video
                  </p>
                  <p className="mt-2 text-sm text-[#6f5a67] break-all">
                    {selectedTemplate?.preview_video_url || selectedTemplate?.background_image || "Generated after previewing the template from Polotno."}
                  </p>
                  {isStarterTemplateUsingBasePreview(selectedTemplate) &&
                  !selectedTemplate?.preview_video_url ? (
                    <p className="mt-2 text-xs leading-5 text-[#9a7588]">
                      Starter templates reuse the base preview until you generate a new preview
                      from Polotno.
                    </p>
                  ) : null}
                </div>
                <div className="rounded-[20px] bg-[#fff7fb] px-4 py-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                    Discovery tags
                  </p>
                  <p className="mt-2 text-sm text-[#6f5a67]">
                    {selectedTemplate?.style_tags?.join(", ") || "Add style tags to improve discovery."}
                  </p>
                  {isStarterTemplateUsingBasePreview(selectedTemplate) ? (
                    <p className="mt-2 text-xs leading-5 text-[#9a7588]">
                      This starter template begins as a clone. Its image and video update after you
                      save changes and regenerate previews.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="mt-4 rounded-[24px] border border-[#f2dce8] bg-[#fff9fc] px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                      Saved asset plan
                    </p>
                    <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                      Persisted rows from <span className="font-bold text-[#4d3040]">template_assets</span>.
                    </p>
                  </div>
                  <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-[#8f6379]">
                    {isLoadingTemplateAssets ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        Loading
                      </>
                    ) : (
                      `${savedTemplateAssets.length} assets`
                    )}
                  </span>
                </div>

                {isLoadingTemplateAssets ? (
                  <div className="mt-4 rounded-[18px] border border-dashed border-[#ead4df] bg-white px-4 py-5 text-sm text-[#8a7080]">
                    Loading saved asset metadata...
                  </div>
                ) : savedTemplateAssets.length ? (
                  <div className="mt-4 space-y-3">
                    {savedTemplateAssets.map((asset) => {
                      const assetKey = getAssetKey(asset);
                      const generationPrompt = getAssetGenerationPrompt(asset);
                      const previewUrl = getAssetPreviewUrl(asset);
                      const mediaKind = getAssetMediaKind(asset);

                      return (
                        <div
                          key={assetKey}
                          className="rounded-[18px] border border-[#f0dde8] bg-white px-4 py-4"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-black text-[#341f2d]">
                                  {getAssetDisplayName(asset)}
                                </p>
                                {asset.asset_type ? (
                                  <span className="rounded-full bg-[#fff1f8] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#a33770]">
                                    {asset.asset_type}
                                  </span>
                                ) : null}
                                {asset.source_strategy ? (
                                  <span className="rounded-full bg-[#f8f0dc] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a6720]">
                                    {formatAssetStrategy(asset.source_strategy)}
                                  </span>
                                ) : null}
                                {asset.status ? (
                                  <span className="rounded-full bg-[#eef8f1] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#277545]">
                                    {formatAssetStrategy(asset.status)}
                                  </span>
                                ) : null}
                              </div>
                              <p className="mt-2 text-xs leading-5 text-[#735f6d]">
                                {asset.description || asset.role || "Asset metadata saved for this template."}
                              </p>
                            </div>

                            {generationPrompt ? (
                              <button
                                type="button"
                                onClick={() => handleCopyAssetPrompt(asset)}
                                className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#ecd2df] bg-white px-3 py-2 text-xs font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                              >
                                <Copy size={13} />
                                {copiedAssetKey === assetKey ? "Copied" : "Copy prompt"}
                              </button>
                            ) : null}
                          </div>

                          <div className="mt-4 overflow-hidden rounded-[18px] border border-[#f3e0ea] bg-[#fffafd]">
                            <div className="flex items-center justify-between gap-3 border-b border-[#f3e0ea] px-3 py-2">
                              <p className="text-xs font-bold text-[#4d3040]">Visual preview</p>
                              {previewUrl ? (
                                <div className="flex shrink-0 items-center gap-2">
                                  <a
                                    href={previewUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center rounded-full border border-[#ecd2df] bg-white px-3 py-1.5 text-[11px] font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                                  >
                                    Open
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleCopyAssetUrl(asset)}
                                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#ecd2df] bg-white px-3 py-1.5 text-[11px] font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                                  >
                                    <Copy size={12} />
                                    {copiedAssetUrlKey === assetKey ? "Copied" : "Copy URL"}
                                  </button>
                                </div>
                              ) : null}
                            </div>

                            {mediaKind === "image" ? (
                              <div className="flex min-h-[180px] items-center justify-center bg-[linear-gradient(45deg,#fff_25%,#fbf0f6_25%,#fbf0f6_50%,#fff_50%,#fff_75%,#fbf0f6_75%,#fbf0f6_100%)] bg-[length:24px_24px] p-4">
                                <img
                                  src={previewUrl}
                                  alt={getAssetDisplayName(asset)}
                                  className="max-h-64 max-w-full rounded-2xl object-contain shadow-sm"
                                />
                              </div>
                            ) : mediaKind === "video" ? (
                              <video
                                src={previewUrl}
                                controls
                                muted
                                className="max-h-72 w-full bg-black object-contain"
                              />
                            ) : mediaKind === "audio" ? (
                              <div className="bg-white px-4 py-5">
                                <audio src={previewUrl} controls className="w-full" />
                              </div>
                            ) : previewUrl ? (
                              <div className="px-4 py-5 text-sm leading-6 text-[#735f6d]">
                                This asset type cannot be previewed inline yet. Use Open to inspect it.
                              </div>
                            ) : (
                              <div className="flex min-h-[140px] items-center justify-center px-4 py-5 text-center text-sm leading-6 text-[#8a7080]">
                                No asset attached yet. Paste a file URL below, save, and the preview will appear here.
                              </div>
                            )}
                          </div>

                          <div className="mt-3 grid gap-2 text-xs leading-5 text-[#735f6d]">
                            <p>
                              <span className="font-bold text-[#4d3040]">Role:</span>{" "}
                              {asset.role || "Not assigned"}
                            </p>
                            <p className="break-all">
                              <span className="font-bold text-[#4d3040]">File:</span>{" "}
                              {asset.file_url || "No generated or uploaded file attached yet."}
                            </p>
                            <p className="break-all">
                              <span className="font-bold text-[#4d3040]">Storage:</span>{" "}
                              {asset.storage_path || "No storage path set."}
                            </p>
                          </div>

                          {generationPrompt ? (
                            <div className="mt-3 rounded-2xl bg-[#fff9fc] px-3 py-3 text-xs leading-5 text-[#6f5a67]">
                              <span className="font-bold text-[#4d3040]">Prompt seed:</span>{" "}
                              {generationPrompt}
                            </div>
                          ) : null}

                          <div className="mt-4 rounded-[18px] border border-[#f3e0ea] bg-[#fffafd] px-3 py-3">
                            <div className="grid gap-3">
                              <label className="space-y-1.5">
                                <span className="text-xs font-bold text-[#4d3040]">File URL</span>
                                <input
                                  value={asset.file_url || ""}
                                  onChange={(event) =>
                                    handleSavedAssetFieldChange(asset.id, "file_url", event.target.value)
                                  }
                                  placeholder="/template-assets/royal-wedding/v1/asset-name.svg"
                                  className="w-full rounded-2xl border border-[#ecd2df] bg-white px-3 py-2 text-xs text-[#4d3040] outline-none transition-colors focus:border-[#ff0099]"
                                />
                              </label>

                              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_150px]">
                                <label className="space-y-1.5">
                                  <span className="text-xs font-bold text-[#4d3040]">Storage path</span>
                                  <input
                                    value={asset.storage_path || ""}
                                    onChange={(event) =>
                                      handleSavedAssetFieldChange(asset.id, "storage_path", event.target.value)
                                    }
                                    placeholder="template-assets/royal-wedding/v1/"
                                    className="w-full rounded-2xl border border-[#ecd2df] bg-white px-3 py-2 text-xs text-[#4d3040] outline-none transition-colors focus:border-[#ff0099]"
                                  />
                                </label>

                                <label className="space-y-1.5">
                                  <span className="text-xs font-bold text-[#4d3040]">Status</span>
                                  <select
                                    value={asset.status || "planned"}
                                    onChange={(event) =>
                                      handleSavedAssetFieldChange(asset.id, "status", event.target.value)
                                    }
                                    className="w-full rounded-2xl border border-[#ecd2df] bg-white px-3 py-2 text-xs text-[#4d3040] outline-none transition-colors focus:border-[#ff0099]"
                                  >
                                    <option value="planned">Planned</option>
                                    <option value="available">Available</option>
                                    <option value="needs_review">Needs Review</option>
                                    <option value="failed">Failed</option>
                                  </select>
                                </label>
                              </div>

                              <label className="space-y-1.5">
                                <span className="text-xs font-bold text-[#4d3040]">Thumbnail URL</span>
                                <input
                                  value={asset.thumbnail_url || ""}
                                  onChange={(event) =>
                                    handleSavedAssetFieldChange(asset.id, "thumbnail_url", event.target.value)
                                  }
                                  placeholder="Optional smaller preview URL"
                                  className="w-full rounded-2xl border border-[#ecd2df] bg-white px-3 py-2 text-xs text-[#4d3040] outline-none transition-colors focus:border-[#ff0099]"
                                />
                              </label>

                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleSaveTemplateAsset(asset)}
                                  disabled={savingTemplateAssetId === asset.id}
                                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2b1d27] px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-[#ff0099] disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {savingTemplateAssetId === asset.id ? (
                                    <>
                                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                      Saving...
                                    </>
                                  ) : (
                                    "Save Asset"
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-4 rounded-[18px] border border-dashed border-[#ead4df] bg-white px-4 py-5 text-sm leading-6 text-[#8a7080]">
                    No saved asset rows yet. Create an AI draft to seed the asset plan for this
                    template.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-[30px] border border-[#edd9e4] bg-white p-6 shadow-[0_18px_50px_rgba(86,43,66,0.08)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                    Metadata editor
                  </p>
                  <h2 className="mt-2 text-2xl font-black text-[#251a24]">
                    Discovery and flow settings
                  </h2>
                </div>
                {isLoadingTemplate ? <Loader2 className="h-5 w-5 animate-spin text-[#b13771]" /> : null}
              </div>

              {statusMessage ? (
                <div
                  className={`mt-5 flex items-start gap-3 rounded-[20px] px-4 py-4 text-sm ${
                    statusTone === "success"
                      ? "bg-[#eefbf3] text-[#17663b]"
                      : statusTone === "error"
                      ? "bg-[#fff1f2] text-[#9f1f3b]"
                      : "bg-[#fff7fb] text-[#735f6d]"
                  }`}
                >
                  {statusTone === "success" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : null}
                  <span>{statusMessage}</span>
                </div>
              ) : null}

              {formState ? (
                <div className="mt-6 space-y-6">
                  <section className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Template Name</span>
                        <input
                          value={formState.name || ""}
                          onChange={(event) => handleFieldChange("name", event.target.value)}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Event Type</span>
                        <select
                          value={formState.event_type || ""}
                          onChange={(event) => handleFieldChange("event_type", event.target.value)}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        >
                          <option value="">Select event type</option>
                          {EVENT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Legacy Category</span>
                        <input
                          value={formState.category || ""}
                          onChange={(event) => handleFieldChange("category", event.target.value)}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Legacy Culture</span>
                        <input
                          value={formState.culture || ""}
                          onChange={(event) => handleFieldChange("culture", event.target.value)}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Legacy Religion</span>
                        <input
                          value={formState.faith_or_religion || ""}
                          onChange={(event) => handleFieldChange("faith_or_religion", event.target.value)}
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                      <Sparkles size={16} />
                      Tags and targeting
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Subevent Types</span>
                        <input
                          value={formState.subevent_types_text || ""}
                          onChange={(event) => handleFieldChange("subevent_types_text", event.target.value)}
                          placeholder="mehendi, sangeet, wedding, reception"
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Style Tags</span>
                        <input
                          value={formState.style_tags_text || ""}
                          onChange={(event) => handleFieldChange("style_tags_text", event.target.value)}
                          placeholder="royal, luxury, traditional"
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Culture Tags</span>
                        <input
                          value={formState.culture_tags_text || ""}
                          onChange={(event) => handleFieldChange("culture_tags_text", event.target.value)}
                          placeholder="Marathi, Punjabi"
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">Religion Tags</span>
                        <input
                          value={formState.religion_tags_text || ""}
                          onChange={(event) => handleFieldChange("religion_tags_text", event.target.value)}
                          placeholder="Hindu, Christian"
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>

                      <label className="space-y-2 md:col-span-2">
                        <span className="text-sm font-bold text-[#4d3040]">Language Tags</span>
                        <input
                          value={formState.language_tags_text || ""}
                          onChange={(event) => handleFieldChange("language_tags_text", event.target.value)}
                          placeholder="English, Hindi, Marathi"
                          className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                        />
                      </label>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                      <Film size={16} />
                      Preview and merchandising
                    </div>

                    <div className="rounded-[20px] border border-[#ecd2df] bg-[#fff9fc] px-4 py-4">
                      <p className="text-sm font-bold text-[#4d3040]">Generated Preview Video</p>
                      <p className="mt-2 text-sm break-all text-[#735f6d]">
                        {formState.preview_video_url || formState.background_image || "This will be generated after you preview the template video from Polotno."}
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handleFieldChange("is_featured", !formState.is_featured)}
                        className={`flex items-center justify-between rounded-[24px] border px-4 py-4 text-left transition-all ${
                          formState.is_featured
                            ? "border-[#ffcc57] bg-[#fff7dc]"
                            : "border-[#ecd2df] bg-white hover:border-[#ffcc57]"
                        }`}
                      >
                        <div>
                          <p className="text-sm font-bold text-[#4d3040]">Featured Template</p>
                          <p className="mt-1 text-xs text-[#8d6d7f]">
                            Prioritize this template in discovery highlights.
                          </p>
                        </div>
                        <Star className={`h-5 w-5 ${formState.is_featured ? "text-[#cc8a00]" : "text-[#d7b46c]"}`} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleFieldChange("is_premium", !formState.is_premium)}
                        className={`flex items-center justify-between rounded-[24px] border px-4 py-4 text-left transition-all ${
                          formState.is_premium
                            ? "border-[#d9b5ff] bg-[#faf2ff]"
                            : "border-[#ecd2df] bg-white hover:border-[#d9b5ff]"
                        }`}
                      >
                        <div>
                          <p className="text-sm font-bold text-[#4d3040]">Premium Template</p>
                          <p className="mt-1 text-xs text-[#8d6d7f]">
                            Mark this design as part of a premium collection.
                          </p>
                        </div>
                        <Crown className={`h-5 w-5 ${formState.is_premium ? "text-[#8c46d8]" : "text-[#b48cd7]"}`} />
                      </button>
                    </div>
                  </section>

                  <div className="flex flex-col gap-3 border-t border-[#f1dfe9] pt-6 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-[#7b6170]">
                      Future step: we can add full template creation here once this metadata workflow feels right.
                    </div>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isSaving || isLoadingTemplate || isAuthLoading}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          Save Metadata
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 rounded-[24px] border border-dashed border-[#eed7e3] bg-[#fff9fc] px-5 py-6 text-sm text-[#8a7080]">
                  Select a template from the left to edit its discovery metadata.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {showLogin ? (
        <LoginModal
          defaultValue=""
          onClose={() => setShowLogin(false)}
          onSuccess={async () => {
            setShowLogin(false);
            if (isAiCreateModalOpen) {
              await submitAiCreateTemplate();
            } else if (isCreateModalOpen) {
              await submitCreateTemplate();
            } else {
              await submitMetadata();
            }
          }}
        />
      ) : null}

      {isAiCreateModalOpen ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/45 px-4 py-6" onClick={() => setIsAiCreateModalOpen(false)}>
          <div
            className="mx-auto flex max-h-[calc(100vh-3rem)] w-[min(980px,100%)] flex-col overflow-hidden rounded-[32px] border border-[#efd9e4] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#f1dfe9] px-6 py-6">
              <div className="max-w-3xl">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                  Admin AI template creation
                </p>
                <h2 className="mt-2 text-3xl font-black text-[#251a24]">
                  Create Template with AI
                </h2>
                <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                  This beta flow is admin-only and spec-driven. We generate a deterministic Polotno
                  draft from the Royal Wedding recipe, then open the shared editor for refinement.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAiCreateModalOpen(false)}
                className="rounded-full p-2 text-[#8f6379] transition-colors hover:bg-[#fff0f7] hover:text-[#ff0099]"
                aria-label="Close create template with AI dialog"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto px-6 py-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
              <div className="space-y-5">
                <div className="rounded-[26px] border border-[#ecd2df] bg-[#fff9fc] px-5 py-5">
                  <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                    <Sparkles size={16} />
                    Request contract
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="space-y-2 md:col-span-2">
                      <span className="text-sm font-bold text-[#4d3040]">Template Name</span>
                      <input
                        value={aiCreateForm.template_name}
                        onChange={(event) => handleAiCreateFieldChange("template_name", event.target.value)}
                        placeholder="Royal Wedding Mobile Video Template v1"
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Event Type</span>
                      <select
                        value={aiCreateForm.event_type}
                        onChange={(event) => handleAiCreateFieldChange("event_type", event.target.value)}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      >
                        <option value="wedding">Wedding</option>
                      </select>
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Style Family</span>
                      <select
                        value={aiCreateForm.style_family}
                        onChange={(event) => handleAiCreateFieldChange("style_family", event.target.value)}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      >
                        <option value="royal">Royal</option>
                      </select>
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Format</span>
                      <select
                        value={aiCreateForm.format}
                        onChange={(event) => handleAiCreateFieldChange("format", event.target.value)}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      >
                        <option value="mobile_video">Mobile Video</option>
                      </select>
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Scene Count</span>
                      <input
                        type="number"
                        min="5"
                        max="5"
                        value={aiCreateForm.scene_count}
                        onChange={(event) => handleAiCreateFieldChange("scene_count", event.target.value)}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className="text-sm font-bold text-[#4d3040]">Style Preset</span>
                      <select
                        value={aiCreateForm.style_preset_id}
                        onChange={(event) => applyAiStylePreset(event.target.value)}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      >
                        {ROYAL_WEDDING_STYLE_PRESETS.map((preset) => (
                          <option key={preset.id} value={preset.id}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs leading-5 text-[#8d6d7f]">
                        Presets are starters only. Admin edits below override the preset before AI
                        generates one draft.
                      </p>
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className="text-sm font-bold text-[#4d3040]">Style Direction</span>
                      <textarea
                        value={aiCreateForm.style_prompt}
                        onChange={(event) => handleAiCreateFieldChange("style_prompt", event.target.value)}
                        placeholder="Describe the design direction, density, mood, and what should feel special."
                        rows={4}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className="text-sm font-bold text-[#4d3040]">Palette Summary</span>
                      <input
                        value={aiCreateForm.palette_text}
                        onChange={(event) => handleAiCreateFieldChange("palette_text", event.target.value)}
                        placeholder="maroon, antique gold, ivory"
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    {[
                      ["palette_primary", "Primary"],
                      ["palette_secondary", "Secondary"],
                      ["palette_accent", "Accent"],
                      ["palette_background", "Background"],
                      ["palette_text_color", "Text"],
                    ].map(([field, label]) => (
                      <label key={field} className="space-y-2">
                        <span className="text-sm font-bold text-[#4d3040]">{label} Color</span>
                        <div className="flex items-center gap-2 rounded-2xl border border-[#ecd2df] bg-white px-3 py-2 focus-within:border-[#ff0099]">
                          <input
                            type="color"
                            value={aiCreateForm[field]}
                            onChange={(event) => handleAiCreateFieldChange(field, event.target.value)}
                            className="h-9 w-10 shrink-0 cursor-pointer rounded-lg border-0 bg-transparent p-0"
                          />
                          <input
                            value={aiCreateForm[field]}
                            onChange={(event) => handleAiCreateFieldChange(field, event.target.value)}
                            className="min-w-0 flex-1 border-0 bg-transparent text-sm outline-none"
                          />
                        </div>
                      </label>
                    ))}

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Copy Style</span>
                      <input
                        value={aiCreateForm.copy_style}
                        onChange={(event) => handleAiCreateFieldChange("copy_style", event.target.value)}
                        placeholder="formal, warm"
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Cultural Context</span>
                      <input
                        value={aiCreateForm.cultural_context_text}
                        onChange={(event) => handleAiCreateFieldChange("cultural_context_text", event.target.value)}
                        placeholder="Indian wedding"
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Mood</span>
                      <input
                        value={aiCreateForm.mood_text}
                        onChange={(event) => handleAiCreateFieldChange("mood_text", event.target.value)}
                        placeholder="regal, cinematic, traditional"
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2">
                      <span className="text-sm font-bold text-[#4d3040]">Ornament Density</span>
                      <select
                        value={aiCreateForm.ornament_density}
                        onChange={(event) => handleAiCreateFieldChange("ornament_density", event.target.value)}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      >
                        <option value="minimal">Minimal</option>
                        <option value="balanced">Balanced</option>
                        <option value="rich">Rich</option>
                        <option value="ornate">Ornate</option>
                      </select>
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className="text-sm font-bold text-[#4d3040]">Typography Direction</span>
                      <input
                        value={aiCreateForm.typography_direction}
                        onChange={(event) => handleAiCreateFieldChange("typography_direction", event.target.value)}
                        placeholder="Elegant serif display typography with clean readable detail text."
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>

                    <label className="space-y-2 md:col-span-2">
                      <span className="text-sm font-bold text-[#4d3040]">Admin Notes</span>
                      <textarea
                        value={aiCreateForm.notes}
                        onChange={(event) => handleAiCreateFieldChange("notes", event.target.value)}
                        placeholder="Optional notes for future AI asset generation."
                        rows={3}
                        className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                      />
                    </label>
                  </div>
                </div>

                <div className="rounded-[26px] border border-[#ecd2df] bg-white px-5 py-5">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                        <PencilRuler size={16} />
                        Asset guidance
                      </div>
                      <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                        Pick library assets as ingredients. AI can place them directly, use them as
                        reference, transform them, or match their style.
                      </p>
                    </div>
                    <span className="rounded-full bg-[#fff1f8] px-3 py-1 text-xs font-bold text-[#a33770]">
                      {(aiCreateForm.selected_assets || []).length} selected
                    </span>
                  </div>

                  <div className="mt-4 max-h-72 space-y-3 overflow-y-auto pr-1">
                    {isLoadingAssetLibrary ? (
                      <div className="rounded-2xl border border-[#f0dde8] bg-[#fff8fb] px-4 py-4 text-sm text-[#735f6d]">
                        Loading reusable assets...
                      </div>
                    ) : assetLibraryItems.length ? (
                      assetLibraryItems.slice(0, 12).map((asset) => {
                        const previewUrl = getAssetPreviewUrl(asset);
                        const selectedAsset = (aiCreateForm.selected_assets || []).find(
                          (item) => String(item.asset_library_item_id) === String(asset.id)
                        );

                        return (
                          <div
                            key={asset.id}
                            className={`rounded-[22px] border px-4 py-4 transition-colors ${
                              selectedAsset
                                ? "border-[#ff0099] bg-[#fff6fb]"
                                : "border-[#f0dde8] bg-white"
                            }`}
                          >
                            <div className="flex gap-3">
                              <button
                                type="button"
                                onClick={() => handleAiAssetToggle(asset)}
                                className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl border border-[#ecd2df] bg-[#fff8fb]"
                              >
                                {previewUrl ? (
                                  <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                                ) : (
                                  <span className="flex h-full w-full items-center justify-center px-2 text-center text-[10px] font-bold uppercase tracking-[0.12em] text-[#a66f8c]">
                                    No Preview
                                  </span>
                                )}
                              </button>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <p className="text-sm font-black text-[#341f2d]">{asset.name}</p>
                                  <button
                                    type="button"
                                    onClick={() => handleAiAssetToggle(asset)}
                                    className={`rounded-full px-3 py-1.5 text-xs font-bold ${
                                      selectedAsset
                                        ? "bg-[#ff0099] text-white"
                                        : "bg-[#fff1f8] text-[#a33770]"
                                    }`}
                                  >
                                    {selectedAsset ? "Selected" : "Use asset"}
                                  </button>
                                </div>
                                <p className="mt-1 text-xs text-[#8d6d7f]">
                                  {asset.asset_type} {asset.usage_tags?.length ? `• ${asset.usage_tags.join(", ")}` : ""}
                                </p>

                                {selectedAsset ? (
                                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                                    <label className="space-y-1">
                                      <span className="text-xs font-bold text-[#4d3040]">Usage</span>
                                      <select
                                        value={selectedAsset.usage_mode}
                                        onChange={(event) =>
                                          handleAiSelectedAssetFieldChange(
                                            asset.id,
                                            "usage_mode",
                                            event.target.value
                                          )
                                        }
                                        className="w-full rounded-xl border border-[#ecd2df] px-3 py-2 text-xs outline-none focus:border-[#ff0099]"
                                      >
                                        <option value="reference_only">Reference only</option>
                                        <option value="place_directly">Place directly</option>
                                        <option value="transform">Transform</option>
                                        <option value="style_match">Style match</option>
                                      </select>
                                    </label>
                                    <label className="space-y-1">
                                      <span className="text-xs font-bold text-[#4d3040]">Role</span>
                                      <input
                                        value={selectedAsset.role}
                                        onChange={(event) =>
                                          handleAiSelectedAssetFieldChange(asset.id, "role", event.target.value)
                                        }
                                        className="w-full rounded-xl border border-[#ecd2df] px-3 py-2 text-xs outline-none focus:border-[#ff0099]"
                                      />
                                    </label>
                                    <label className="space-y-1 md:col-span-2">
                                      <span className="text-xs font-bold text-[#4d3040]">Instruction</span>
                                      <textarea
                                        value={selectedAsset.instruction}
                                        onChange={(event) =>
                                          handleAiSelectedAssetFieldChange(
                                            asset.id,
                                            "instruction",
                                            event.target.value
                                          )
                                        }
                                        rows={2}
                                        placeholder="Use this as reference but make it brighter and more gold."
                                        className="w-full rounded-xl border border-[#ecd2df] px-3 py-2 text-xs outline-none focus:border-[#ff0099]"
                                      />
                                    </label>
                                    <label className="space-y-1 md:col-span-2">
                                      <span className="text-xs font-bold text-[#4d3040]">Target Scenes</span>
                                      <input
                                        value={selectedAsset.target_scenes_text}
                                        onChange={(event) =>
                                          handleAiSelectedAssetFieldChange(
                                            asset.id,
                                            "target_scenes_text",
                                            event.target.value
                                          )
                                        }
                                        placeholder="grand_opening, ceremony_details"
                                        className="w-full rounded-xl border border-[#ecd2df] px-3 py-2 text-xs outline-none focus:border-[#ff0099]"
                                      />
                                    </label>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-[#f0dde8] bg-[#fff8fb] px-4 py-4 text-sm leading-6 text-[#735f6d]">
                        No reusable library assets are available yet. The draft can still be
                        generated from the style brief and registry roles.
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => handleAiCreateFieldChange("is_featured", !aiCreateForm.is_featured)}
                    className={`flex items-center justify-between rounded-[24px] border px-4 py-4 text-left transition-all ${
                      aiCreateForm.is_featured
                        ? "border-[#ffcc57] bg-[#fff7dc]"
                        : "border-[#ecd2df] bg-white hover:border-[#ffcc57]"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#4d3040]">Featured Template</p>
                      <p className="mt-1 text-xs text-[#8d6d7f]">Surface this draft in curated rows.</p>
                    </div>
                    <Star className={`h-5 w-5 ${aiCreateForm.is_featured ? "text-[#cc8a00]" : "text-[#d7b46c]"}`} />
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAiCreateFieldChange("is_premium", !aiCreateForm.is_premium)}
                    className={`flex items-center justify-between rounded-[24px] border px-4 py-4 text-left transition-all ${
                      aiCreateForm.is_premium
                        ? "border-[#d9b5ff] bg-[#faf2ff]"
                        : "border-[#ecd2df] bg-white hover:border-[#d9b5ff]"
                    }`}
                  >
                    <div>
                      <p className="text-sm font-bold text-[#4d3040]">Premium Template</p>
                      <p className="mt-1 text-xs text-[#8d6d7f]">Mark this draft as premium from day one.</p>
                    </div>
                    <Crown className={`h-5 w-5 ${aiCreateForm.is_premium ? "text-[#8c46d8]" : "text-[#b48cd7]"}`} />
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-[26px] border border-[#ecd2df] bg-white px-5 py-5">
                  <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em] text-[#a66f8c]">
                    <PencilRuler size={16} />
                    Planned output
                  </div>
                  <p className="mt-3 text-sm leading-6 text-[#735f6d]">
                    This beta flow currently resolves one spec family:
                    <span className="font-semibold text-[#4d3040]"> Royal Wedding Mobile Video v1</span>.
                    We turn this creative brief into one starter draft, then hand you off to
                    Polotno for admin refinement.
                  </p>

                  <div className="mt-5 rounded-[22px] bg-[#fff8fb] px-4 py-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                      Scene plan
                    </p>
                    <div className="mt-3 space-y-3">
                      {aiScenePreview.map((scene, index) => (
                        <div
                          key={scene.id}
                          className="rounded-[18px] border border-[#f0dde8] bg-white px-4 py-3"
                        >
                          <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#b07c98]">
                            Scene {index + 1}
                          </p>
                          <p className="mt-1 text-sm font-bold text-[#341f2d]">{scene.label}</p>
                          <p className="mt-1 text-sm text-[#735f6d]">{scene.purpose}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 rounded-[22px] bg-[#fff8fb] px-4 py-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                      Draft characteristics
                    </p>
                    <ul className="mt-3 space-y-2 text-sm leading-6 text-[#735f6d]">
                      <li>1080 x 1920 portrait Polotno canvas</li>
                      <li>5 named scenes with editable wedding placeholders</li>
                      <li>Palette and style direction sent as AI planning constraints</li>
                      <li>Selected library assets preserved as placement or reference guidance</li>
                    </ul>
                  </div>

                  <div className="mt-5 rounded-[22px] bg-[#fff8fb] px-4 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                          Asset plan
                        </p>
                        <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                          Registry-driven assets for generation, reuse, and Polotno placement.
                        </p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-[#8f6379]">
                        {aiAssetPlan.length} roles
                      </span>
                    </div>

                    <div className="mt-4 space-y-3">
                      {aiAssetPlan.map((asset) => (
                        <div
                          key={asset.assetKey}
                          className="rounded-[18px] border border-[#f0dde8] bg-white px-4 py-4"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="text-sm font-black text-[#341f2d]">{asset.displayName}</p>
                                <span className="rounded-full bg-[#fff1f8] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#a33770]">
                                  {asset.type}
                                </span>
                                <span className="rounded-full bg-[#f8f0dc] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a6720]">
                                  {formatAssetStrategy(asset.sourceStrategy)}
                                </span>
                              </div>
                              <p className="mt-2 text-xs leading-5 text-[#735f6d]">{asset.description}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCopyAssetPrompt(asset)}
                              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#ecd2df] bg-white px-3 py-2 text-xs font-bold text-[#7f2556] transition-colors hover:border-[#ff0099] hover:text-[#ff0099]"
                            >
                              <Copy size={13} />
                              {copiedAssetKey === getAssetKey(asset) ? "Copied" : "Copy prompt"}
                            </button>
                          </div>

                          <div className="mt-3 grid gap-2 text-xs leading-5 text-[#735f6d]">
                            <p>
                              <span className="font-bold text-[#4d3040]">Slot:</span>{" "}
                              {asset.polotnoMapping?.slot || "Not mapped"}
                            </p>
                            <p>
                              <span className="font-bold text-[#4d3040]">Storage:</span>{" "}
                              {asset.storagePath}
                            </p>
                            <p>
                              <span className="font-bold text-[#4d3040]">Scenes:</span>{" "}
                              {asset.sceneUsage.length ? asset.sceneUsage.join(", ") : "Not assigned"}
                            </p>
                          </div>

                          {asset.generationPrompt ? (
                            <div className="mt-3 rounded-2xl bg-[#fff9fc] px-3 py-3 text-xs leading-5 text-[#6f5a67]">
                              <span className="font-bold text-[#4d3040]">Prompt seed:</span>{" "}
                              {asset.generationPrompt}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-[24px] border border-[#f0dde8] bg-[#fff8fb] px-4 py-4 text-sm leading-6 text-[#735f6d]">
                  This is intentionally admin-only and conservative. We create a strong starter,
                  not a locked final design. Admins can refine the result in Polotno before saving
                  or publishing.
                </div>
              </div>
            </div>

            <div className="flex shrink-0 flex-col gap-3 border-t border-[#f1dfe9] bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-[#7b6170]">
                Next step after creation: refine the seeded draft in Polotno, then generate the real
                preview video.
              </div>
              <button
                type="button"
                onClick={submitAiCreateTemplate}
                disabled={isAiCreating}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isAiCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Building draft...
                  </>
                ) : (
                  <>
                    Create AI Draft & Open Editor
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {isCreateModalOpen ? (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/45 px-4 py-6" onClick={() => setIsCreateModalOpen(false)}>
          <div
            className="mx-auto flex max-h-[calc(100vh-3rem)] w-[min(720px,100%)] flex-col overflow-hidden rounded-[32px] border border-[#efd9e4] bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#f1dfe9] px-6 py-6">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b07c98]">
                  Admin template creation
                </p>
                <h2 className="mt-2 text-3xl font-black text-[#251a24]">Create a blank template</h2>
                <p className="mt-2 text-sm leading-6 text-[#735f6d]">
                  Start with discovery metadata, then open a blank mobile Polotno canvas to design
                  the actual invitation template manually.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateModalOpen(false)}
                className="rounded-full p-2 text-[#8f6379] transition-colors hover:bg-[#fff0f7] hover:text-[#ff0099]"
                aria-label="Close create template dialog"
              >
                <X size={20} />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 md:col-span-2">
                <span className="text-sm font-bold text-[#4d3040]">Template Name</span>
                <input
                  value={createForm.name}
                  onChange={(event) => handleCreateFieldChange("name", event.target.value)}
                  placeholder="Royal Marathi Wedding Film"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Event Type</span>
                <select
                  value={createForm.event_type}
                  onChange={(event) => handleCreateFieldChange("event_type", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                >
                  {EVENT_TYPE_OPTIONS.map((option) => (
                    <option key={`create-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Legacy Category</span>
                <input
                  value={createForm.category}
                  onChange={(event) => handleCreateFieldChange("category", event.target.value)}
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Style Tags</span>
                <input
                  value={createForm.style_tags_text}
                  onChange={(event) => handleCreateFieldChange("style_tags_text", event.target.value)}
                  placeholder="royal, luxury, traditional"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Subevent Types</span>
                <input
                  value={createForm.subevent_types_text}
                  onChange={(event) => handleCreateFieldChange("subevent_types_text", event.target.value)}
                  placeholder="mehendi, sangeet, wedding, reception"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Culture Tags</span>
                <input
                  value={createForm.culture_tags_text}
                  onChange={(event) => handleCreateFieldChange("culture_tags_text", event.target.value)}
                  placeholder="Marathi, Punjabi"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Religion Tags</span>
                <input
                  value={createForm.religion_tags_text}
                  onChange={(event) => handleCreateFieldChange("religion_tags_text", event.target.value)}
                  placeholder="Hindu"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

              <label className="space-y-2">
                <span className="text-sm font-bold text-[#4d3040]">Language Tags</span>
                <input
                  value={createForm.language_tags_text}
                  onChange={(event) => handleCreateFieldChange("language_tags_text", event.target.value)}
                  placeholder="English, Marathi"
                  className="w-full rounded-2xl border border-[#ecd2df] px-4 py-3 text-sm outline-none transition-colors focus:border-[#ff0099]"
                />
              </label>

            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <button
                type="button"
                onClick={() => handleCreateFieldChange("is_featured", !createForm.is_featured)}
                className={`flex items-center justify-between rounded-[24px] border px-4 py-4 text-left transition-all ${
                  createForm.is_featured
                    ? "border-[#ffcc57] bg-[#fff7dc]"
                    : "border-[#ecd2df] bg-white hover:border-[#ffcc57]"
                }`}
              >
                <div>
                  <p className="text-sm font-bold text-[#4d3040]">Featured Template</p>
                  <p className="mt-1 text-xs text-[#8d6d7f]">Use this in homepage highlights.</p>
                </div>
                <Star className={`h-5 w-5 ${createForm.is_featured ? "text-[#cc8a00]" : "text-[#d7b46c]"}`} />
              </button>

              <button
                type="button"
                onClick={() => handleCreateFieldChange("is_premium", !createForm.is_premium)}
                className={`flex items-center justify-between rounded-[24px] border px-4 py-4 text-left transition-all ${
                  createForm.is_premium
                    ? "border-[#d9b5ff] bg-[#faf2ff]"
                    : "border-[#ecd2df] bg-white hover:border-[#d9b5ff]"
                }`}
              >
                <div>
                  <p className="text-sm font-bold text-[#4d3040]">Premium Template</p>
                  <p className="mt-1 text-xs text-[#8d6d7f]">Mark this as premium content.</p>
                </div>
                <Crown className={`h-5 w-5 ${createForm.is_premium ? "text-[#8c46d8]" : "text-[#b48cd7]"}`} />
              </button>
            </div>
            </div>

            <div className="flex shrink-0 flex-col gap-3 border-t border-[#f1dfe9] bg-white px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-[#7b6170]">
                This creates a blank vertical mobile template record and opens Polotno for manual design.
              </div>
              <button
                type="button"
                onClick={submitCreateTemplate}
                disabled={isCreating}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#ff0099] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-[#ff0099]/20 transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Blank & Open Editor
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminTemplateMetadataPage;
